import { FC, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSendMfaCodeMutation } from '@store/rtk/auth';
import {
  clearStoredChallenge,
  getStoredChallenge,
  METHOD_LABELS,
  updateStoredChallenge,
} from './constants';
import { VerificationCodeLayout } from './VerificationCodeLayout';
import { toastError } from '@utils/helpers';
import { toastMessage } from '@utils/toastMessage';

export const GetVerificationCode: FC = () => {
  const navigate = useNavigate();
  const challenge = getStoredChallenge();
  const availableMethods = challenge?.availableMethods ?? [];
  const defaultMethod = availableMethods.includes('TEXT_MESSAGE')
    ? 'TEXT_MESSAGE'
    : availableMethods[0];

  const [method, setMethod] = useState(defaultMethod);
  const [sendMfaCode, { isLoading }] = useSendMfaCodeMutation();

  useEffect(() => {
    if (!challenge?.challengeToken) {
      navigate('/authorization/login', { replace: true });
    }
  }, [challenge?.challengeToken, navigate]);

  if (!challenge?.challengeToken) return null;

  const handleRequestCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!method) return;

    try {
      const result = await sendMfaCode({
        challengeToken: challenge.challengeToken,
        method,
      }).unwrap();

      updateStoredChallenge({
        challengeToken: result.challengeToken,
        method: result.method,
        maskedContact: result.maskedContact,
        expiresAt: result.expiresAt,
        remainingCodeRequests: result.remainingCodeRequests,
      });
      navigate('/authorization/enter-verification-code');
    } catch (err: any) {
      toastError(err, toastMessage.AUTH.failedToSendVerificationCode);
    }
  };

  const handleCancel = () => {
    clearStoredChallenge();
    navigate('/authorization/login');
  };

  return (
    <VerificationCodeLayout>
      <form className="verification-code__form" onSubmit={handleRequestCode}>
        <h1>Get Verification Code</h1>

        <fieldset className="verification-code__methods">
          <legend>How would you like to receive it?</legend>

          {(['TEXT_MESSAGE', 'EMAIL'] as const).map((option) => {
            const disabled = !availableMethods.includes(option);
            return (
              <label
                key={option}
                className={`verification-code__method${disabled ? ' verification-code__method--disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="verificationMethod"
                  value={option}
                  checked={method === option}
                  disabled={disabled}
                  onChange={() => setMethod(option)}
                />
                <span>{METHOD_LABELS[option]}</span>
              </label>
            );
          })}
        </fieldset>

        <p className="verification-code__hint">
          The code expires in 10 minutes after you request it.
        </p>

        <div className="verification-code__actions">
          <button
            className="verification-code__button"
            type="submit"
            disabled={!method || isLoading}
          >
            Next
          </button>
          <button
            className="verification-code__button verification-code__button--cancel"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </form>
    </VerificationCodeLayout>
  );
};
